/**
 * A stable per-browser id for rate limiting the public photo endpoints.
 *
 * The server used to budget searches per IP address, which is fine until you
 * remember that mobile carriers put hundreds of subscribers behind one address.
 * At a race, that is most of the field. This gives each browser its own budget
 * so one person searching cannot spend everybody else's allowance.
 *
 * It is deliberately NOT an identifier of a person. It is random, it is not sent
 * anywhere except our own rate-limit check, it survives only as long as the
 * browser keeps localStorage, and nothing is stored against it server-side: the
 * server hashes it into a Redis counter key and forgets it. A user clearing site
 * data simply gets a new budget, which is the correct outcome.
 *
 * Because it is client-supplied it can be forged or rotated, so it is only ever
 * the fairness dimension. The per-IP cap still applies underneath it, and a
 * request arriving without one falls back to the original strict per-IP limit.
 */

const STORAGE_KEY = 'fv.photo.client';

/** Matches the server's validation in lib/rate-limit.ts. */
function isWellFormed(value: string): boolean {
  return /^[A-Za-z0-9_-]{8,64}$/.test(value);
}

function generate(): string {
  // crypto.randomUUID is available in every browser that can reach the finder;
  // the fallback keeps SSR and ancient webviews from throwing.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * The id for this browser, creating one on first use. Returns null on the
 * server, and degrades to a fresh throwaway id when storage is unavailable
 * (private mode, storage disabled), which costs that visitor nothing.
 */
export function getPhotoClientId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing && isWellFormed(existing)) return existing;
    const fresh = generate();
    window.localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return generate();
  }
}

/**
 * Request headers for a public photo call. Spread this instead of writing the
 * Content-Type literal, so a new endpoint cannot quietly miss the id and drop
 * its callers onto the strict per-IP limit.
 */
export function photoRequestHeaders(json = true): Record<string, string> {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';
  const id = getPhotoClientId();
  if (id) headers['x-fv-client'] = id;
  return headers;
}
